import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  Users, MessageCircle, Plus, Edit2, Trash2, X, Copy, Search,
  UserPlus, Send, UserMinus, ExternalLink
} from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  beginner: 'bg-green-100 text-green-800',
  intermediate: 'bg-sky-100 text-sky-800',
  advanced: 'bg-purple-100 text-purple-800',
  custom: 'bg-gray-100 text-gray-800',
};

const LEVEL_LABELS = {
  beginner: { tr: 'Başlangıç', en: 'Beginner' },
  intermediate: { tr: 'Orta', en: 'Intermediate' },
  advanced: { tr: 'İleri', en: 'Advanced' },
  custom: { tr: 'Özel', en: 'Custom' },
};

const initialForm = {
  name: '',
  name_en: '',
  level: 'beginner',
  capacity: 50,
  whatsapp_invite_url: '',
  description_tr: '',
  description_en: '',
};

export default function AdminCabinCrewGroups() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [membersModal, setMembersModal] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      const res = await axios.get(`${API}/admin/cabin-crew-groups`, {
        headers: authHeaders(),
        params,
      });
      setGroups(res.data.groups || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, lang, toast, levelFilter]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const resetModal = () => {
    setShowCreateModal(false);
    setEditingGroup(null);
    setForm(initialForm);
  };

  const openEdit = (group) => {
    setEditingGroup(group);
    setForm({
      name: group.name || '',
      name_en: group.name_en || '',
      level: group.level,
      capacity: group.capacity,
      whatsapp_invite_url: group.whatsapp_invite_url || '',
      description_tr: group.description_tr || '',
      description_en: group.description_en || '',
    });
    setShowCreateModal(true);
  };

  const submitForm = async () => {
    if (!form.name || form.name.length < 3) {
      toast.warning(lang === 'tr' ? 'Grup adi en az 3 karakter olmali' : 'Group name at least 3 chars');
      return;
    }
    if (!form.whatsapp_invite_url.startsWith('https://chat.whatsapp.com/')) {
      toast.warning(lang === 'tr' ? 'Geçerli bir WhatsApp grup linki girin' : 'Enter valid WhatsApp group link');
      return;
    }
    const capacity = parseInt(form.capacity, 10);
    if (isNaN(capacity) || capacity < 10 || capacity > 100) {
      toast.warning(lang === 'tr' ? 'Kapasite 10-100 arasinda olmali' : 'Capacity must be 10-100');
      return;
    }

    setSubmitting(true);
    try {
      const body = { ...form, capacity };
      if (editingGroup) {
        await axios.put(
          `${API}/admin/cabin-crew-groups/${editingGroup.group_id}`,
          body,
          { headers: authHeaders() }
        );
        toast.success(lang === 'tr' ? 'Grup güncellendi' : 'Group updated');
      } else {
        await axios.post(`${API}/admin/cabin-crew-groups`, body, { headers: authHeaders() });
        toast.success(lang === 'tr' ? 'Grup oluşturuldu' : 'Group created');
      }
      resetModal();
      fetchGroups();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (group) => {
    if (!window.confirm(
      lang === 'tr'
        ? `"${group.name}" grubunu pasiflestirmek istediginize emin misiniz? Uyeler otomatik cikarilmaz.`
        : `Deactivate group "${group.name}"? Members will not be removed.`
    )) return;
    try {
      await axios.delete(`${API}/admin/cabin-crew-groups/${group.group_id}`, { headers: authHeaders() });
      toast.success(lang === 'tr' ? 'Grup pasiflestirildi' : 'Group deactivated');
      fetchGroups();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const copyInvite = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(lang === 'tr' ? 'Link kopyalandı' : 'Link copied');
    } catch {
      toast.error(lang === 'tr' ? 'Kopyalama başarısız' : 'Copy failed');
    }
  };

  const openMembers = async (group) => {
    try {
      const res = await axios.get(
        `${API}/admin/cabin-crew-groups/${group.group_id}/members`,
        { headers: authHeaders() }
      );
      setMembersModal({ group: res.data.group, members: res.data.members });
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const refreshMembers = async () => {
    if (!membersModal) return;
    try {
      const res = await axios.get(
        `${API}/admin/cabin-crew-groups/${membersModal.group.group_id}/members`,
        { headers: authHeaders() }
      );
      setMembersModal({ group: res.data.group, members: res.data.members });
      fetchGroups();
    } catch {
      // ignore
    }
  };

  const totalActive = groups.filter(g => g.active).length;
  const totalMembers = groups.reduce((s, g) => s + (g.current_count || 0), 0);
  const totalCapacity = groups.reduce((s, g) => s + (g.capacity || 0), 0);

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: lang === 'tr' ? 'Öğrenciler' : 'Students', href: '/admin/students' },
          { label: lang === 'tr' ? 'Gruplar' : 'Groups' },
        ]}
        title={lang === 'tr' ? 'Kabin Memurluğu Grupları' : 'Cabin Crew Groups'}
        subtitle={lang === 'tr' ? 'WhatsApp gruplarını ve üyeliklerini yönet' : 'Manage WhatsApp groups and memberships'}
        actions={
          <button
            onClick={() => { setEditingGroup(null); setForm(initialForm); setShowCreateModal(true); }}
            className="btn-primary px-4 py-2 flex items-center gap-2"
            data-testid="create-group-btn"
          >
            <Plus size={18} />
            {lang === 'tr' ? 'Yeni Grup' : 'New Group'}
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard value={groups.length} label={lang === 'tr' ? 'Toplam' : 'Total'} color="gray" />
        <StatCard value={totalActive} label={lang === 'tr' ? 'Aktif' : 'Active'} color="green" />
        <StatCard value={`${totalMembers}/${totalCapacity}`} label={lang === 'tr' ? 'Doluluk' : 'Occupancy'} color="sky" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'beginner', 'intermediate', 'advanced', 'custom'].map(f => (
          <button
            key={f}
            onClick={() => setLevelFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
              levelFilter === f
                ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
            data-testid={`filter-${f}`}
          >
            {f === 'all'
              ? (lang === 'tr' ? 'Tumu' : 'All')
              : (lang === 'tr' ? LEVEL_LABELS[f].tr : LEVEL_LABELS[f].en)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card-clay p-8 text-center">
          <div className="w-8 h-8 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : groups.length === 0 ? (
        <div className="card-clay p-12 text-center text-[var(--text-muted)]">
          {lang === 'tr' ? 'Henuz grup yok. "Yeni Grup" butonuna basin.' : 'No groups yet. Press "New Group".'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="groups-list">
          {groups.map(g => (
            <GroupCard
              key={g.group_id}
              group={g}
              lang={lang}
              onEdit={() => openEdit(g)}
              onMembers={() => openMembers(g)}
              onCopy={() => copyInvite(g.whatsapp_invite_url)}
              onDeactivate={() => deactivate(g)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <GroupFormModal
          lang={lang}
          editing={editingGroup}
          form={form}
          setForm={setForm}
          submitting={submitting}
          onSubmit={submitForm}
          onClose={resetModal}
        />
      )}

      {membersModal && (
        <MembersModal
          lang={lang}
          group={membersModal.group}
          members={membersModal.members}
          authHeaders={authHeaders}
          toast={toast}
          onClose={() => setMembersModal(null)}
          onChange={refreshMembers}
        />
      )}
    </AdminLayout>
  );
}

function StatCard({ value, label, color }) {
  const colorClass = {
    green: 'border-green-200 text-green-700',
    sky: 'border-sky-200 text-sky-700',
    gray: 'border-gray-200 text-gray-700',
  }[color] || 'border-gray-200 text-gray-700';
  return (
    <div className={`bg-white rounded-xl p-4 border-2 ${colorClass}`}>
      <div className="text-xs text-[var(--text-muted)]">{label}</div>
      <div className="font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

function GroupCard({ group, lang, onEdit, onMembers, onCopy, onDeactivate }) {
  const levelStyle = LEVEL_STYLES[group.level] || LEVEL_STYLES.custom;
  const levelLabel = LEVEL_LABELS[group.level] ? (lang === 'tr' ? LEVEL_LABELS[group.level].tr : LEVEL_LABELS[group.level].en) : group.level;
  const occupancy = group.capacity ? Math.round((group.current_count / group.capacity) * 100) : 0;

  return (
    <div
      className={`bg-white rounded-2xl p-4 border-2 ${group.active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}
      data-testid={`group-${group.group_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <MessageCircle size={18} className="text-green-600 flex-shrink-0" />
            <h3 className="font-bold truncate">{lang === 'tr' ? group.name : (group.name_en || group.name)}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${levelStyle}`}>
              {levelLabel}
            </span>
            {!group.active && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                {lang === 'tr' ? 'Pasif' : 'Inactive'}
              </span>
            )}
          </div>
          <p className="text-[11px] text-[var(--text-muted)] truncate">
            {group.description_tr && lang === 'tr' ? group.description_tr : (group.description_en || '-')}
          </p>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-[var(--text-muted)]">{lang === 'tr' ? 'Doluluk' : 'Occupancy'}</span>
          <span className="font-bold">{group.current_count}/{group.capacity}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${occupancy >= 90 ? 'bg-red-400' : occupancy >= 70 ? 'bg-amber-400' : 'bg-green-400'}`}
            style={{ width: `${occupancy}%` }}
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 text-xs">
          <MessageCircle size={14} className="text-green-600 flex-shrink-0" />
          <span className="truncate flex-1 text-[11px]">{group.whatsapp_invite_url}</span>
          <button onClick={onCopy} title={lang === 'tr' ? 'Kopyala' : 'Copy'} className="text-green-700 hover:text-green-900" data-testid={`copy-${group.group_id}`}>
            <Copy size={14} />
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onMembers}
          className="px-3 py-1.5 text-xs font-bold rounded-lg bg-sky-100 text-sky-700 hover:bg-sky-200 flex items-center gap-1"
          data-testid={`members-${group.group_id}`}
        >
          <Users size={12} /> {lang === 'tr' ? 'Uyeler' : 'Members'}
        </button>
        <button
          onClick={onEdit}
          className="px-3 py-1.5 text-xs font-bold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1"
          data-testid={`edit-${group.group_id}`}
        >
          <Edit2 size={12} /> {lang === 'tr' ? 'Düzenle' : 'Edit'}
        </button>
        {group.active && (
          <button
            onClick={onDeactivate}
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1 ml-auto"
            data-testid={`deactivate-${group.group_id}`}
          >
            <Trash2 size={12} /> {lang === 'tr' ? 'Pasiflestir' : 'Deactivate'}
          </button>
        )}
      </div>
    </div>
  );
}

function GroupFormModal({ lang, editing, form, setForm, submitting, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="group-form-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">
            {editing
              ? (lang === 'tr' ? 'Grubu Düzenle' : 'Edit Group')
              : (lang === 'tr' ? 'Yeni Grup' : 'New Group')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold block mb-1">
              {lang === 'tr' ? 'Grup Adi (TR) *' : 'Group Name (TR) *'}
            </label>
            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
              data-testid="form-name"
              placeholder={lang === 'tr' ? 'Örnek: Başlangıç Grubu 1' : 'Example: Beginner Group 1'}
            />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1">Group Name (EN)</label>
            <input
              value={form.name_en}
              onChange={e => setForm({ ...form, name_en: e.target.value })}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
              placeholder="Beginner Group 1"
            />
          </div>

          {!editing && (
            <div>
              <label className="text-xs font-bold block mb-1">
                {lang === 'tr' ? 'Seviye *' : 'Level *'}
              </label>
              <select
                value={form.level}
                onChange={e => setForm({ ...form, level: e.target.value })}
                className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
                data-testid="form-level"
              >
                <option value="beginner">{lang === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                <option value="intermediate">{lang === 'tr' ? 'Orta' : 'Intermediate'}</option>
                <option value="advanced">{lang === 'tr' ? 'İleri' : 'Advanced'}</option>
                <option value="custom">{lang === 'tr' ? 'Özel' : 'Custom'}</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-bold block mb-1">
              {lang === 'tr' ? 'Kapasite (10-100) *' : 'Capacity (10-100) *'}
            </label>
            <input
              type="number"
              min="10"
              max="100"
              value={form.capacity}
              onChange={e => setForm({ ...form, capacity: e.target.value })}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
              data-testid="form-capacity"
            />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1">
              {lang === 'tr' ? 'WhatsApp Grup Linki *' : 'WhatsApp Group Link *'}
            </label>
            <input
              value={form.whatsapp_invite_url}
              onChange={e => setForm({ ...form, whatsapp_invite_url: e.target.value })}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
              placeholder="https://chat.whatsapp.com/..."
              data-testid="form-whatsapp"
            />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1">
              {lang === 'tr' ? 'Açıklama (TR)' : 'Description (TR)'}
            </label>
            <textarea
              value={form.description_tr}
              onChange={e => setForm({ ...form, description_tr: e.target.value })}
              rows={2}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-bold block mb-1">Description (EN)</label>
            <textarea
              value={form.description_en}
              onChange={e => setForm({ ...form, description_en: e.target.value })}
              rows={2}
              className="w-full p-2 border-2 border-gray-300 rounded-lg text-sm"
            />
          </div>

          <button
            onClick={onSubmit}
            disabled={submitting}
            className="btn-primary w-full py-2 disabled:opacity-50"
            data-testid="form-submit"
          >
            {submitting
              ? (lang === 'tr' ? 'Kaydediliyor...' : 'Saving...')
              : editing
                ? (lang === 'tr' ? 'Guncelle' : 'Update')
                : (lang === 'tr' ? 'Oluştur' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

function MembersModal({ lang, group, members, authHeaders, toast, onClose, onChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);

  const searchUsers = async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); setSuggestion(null); return; }
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { search: q, role: 'student', limit: 10 },
        headers: authHeaders(),
      });
      setSearchResults((res.data.users || []).slice(0, 10));
    } catch {
      // ignore
    }
  };

  const fetchSuggest = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/cabin-crew-groups/suggest/${userId}`, { headers: authHeaders() });
      setSuggestion(res.data);
    } catch {
      setSuggestion(null);
    }
  };

  const assign = async (userId, sendInvite) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/cabin-crew-groups/${group.group_id}/assign`,
        { user_id: userId, send_invite: sendInvite },
        { headers: authHeaders() }
      );
      toast.success(
        res.data.invite_sent
          ? (lang === 'tr' ? 'Atandı ve davet gönderildi' : 'Assigned and invite sent')
          : (lang === 'tr' ? 'Atandı' : 'Assigned')
      );
      setSearchQuery('');
      setSearchResults([]);
      setSuggestion(null);
      onChange();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId) => {
    if (!window.confirm(lang === 'tr' ? 'Bu öğrenciyi gruptan çıkar?' : 'Remove this student from group?')) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/admin/cabin-crew-groups/${group.group_id}/members/${userId}`,
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Cikartildi' : 'Removed');
      onChange();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (userId) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/cabin-crew-groups/${group.group_id}/members/${userId}/resend-invite`,
        {},
        { headers: authHeaders() }
      );
      toast.success(
        res.data.sent
          ? (lang === 'tr' ? 'Davet gönderildi' : 'Invite sent')
          : (lang === 'tr' ? 'Gönderilemedi' : 'Failed to send')
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="members-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">
              {lang === 'tr' ? 'Grup Uyeleri' : 'Group Members'}
            </h2>
            <p className="text-xs text-[var(--text-muted)]">{group.name} ({members.length}/{group.capacity})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <X size={20} />
          </button>
        </div>

        {/* Add member */}
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 mb-4">
          <div className="font-bold text-sm mb-2 flex items-center gap-2">
            <UserPlus size={16} />
            {lang === 'tr' ? 'Yeni Uye Ekle' : 'Add Member'}
          </div>
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-2">
            <Search size={14} className="text-gray-400" />
            <input
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); searchUsers(e.target.value); setSuggestion(null); }}
              className="flex-1 p-2 text-sm outline-none"
              placeholder={lang === 'tr' ? 'Öğrenci ad/email ara...' : 'Search student name/email...'}
              data-testid="add-member-search"
            />
          </div>

          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y mt-2 max-h-40 overflow-y-auto">
              {searchResults.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => { fetchSuggest(u.user_id); setSearchQuery(`${u.name} (${u.email})`); setSearchResults([]); }}
                  className="w-full text-left p-2 hover:bg-gray-50 text-sm"
                >
                  <div className="font-bold">{u.name}</div>
                  <div className="text-[11px] text-gray-500">{u.email}</div>
                </button>
              ))}
            </div>
          )}

          {suggestion && (
            <div className="mt-2 text-xs">
              <div className="bg-gray-50 border rounded p-2 mb-2">
                <p className="font-bold">{suggestion.user?.name}</p>
                <p className="text-[11px]">
                  {lang === 'tr' ? 'Seviye' : 'Level'}: {suggestion.user?.cabin_crew_level || '-'} ·{' '}
                  {lang === 'tr' ? 'Program' : 'Program'}: {suggestion.user?.has_enrollment ? '✓' : '✗'}
                </p>
                {suggestion.reason !== 'ok' && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    {suggestion[`message_${lang}`] || suggestion.message_tr || suggestion.reason}
                  </p>
                )}
              </div>
              {suggestion.reason === 'ok' && suggestion.user?.cabin_crew_level === group.level && (
                <div className="flex gap-2">
                  <button
                    onClick={() => assign(suggestion.user.user_id, false)}
                    disabled={busy}
                    className="flex-1 py-1.5 text-xs font-bold rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                    data-testid="assign-no-invite"
                  >
                    {lang === 'tr' ? 'Ata' : 'Assign'}
                  </button>
                  <button
                    onClick={() => assign(suggestion.user.user_id, true)}
                    disabled={busy}
                    className="flex-1 py-1.5 text-xs font-bold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    data-testid="assign-with-invite"
                  >
                    <Send size={12} className="inline mr-1" />
                    {lang === 'tr' ? 'Ata + Davet' : 'Assign + Invite'}
                  </button>
                </div>
              )}
              {suggestion.reason === 'ok' && suggestion.user?.cabin_crew_level !== group.level && (
                <p className="text-[11px] text-red-600 font-bold">
                  {lang === 'tr'
                    ? `Seviye uyuşmazlığı: öğrenci ${suggestion.user?.cabin_crew_level}, grup ${group.level}`
                    : `Level mismatch: student ${suggestion.user?.cabin_crew_level}, group ${group.level}`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Members list */}
        <div className="space-y-2">
          {members.length === 0 ? (
            <div className="text-center text-sm text-[var(--text-muted)] py-6">
              {lang === 'tr' ? 'Henuz uye yok' : 'No members yet'}
            </div>
          ) : (
            members.map(m => (
              <div key={m.user_id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{m.name}</p>
                  <p className="text-[11px] text-[var(--text-muted)] truncate">{m.email} · {m.phone || '-'}</p>
                  <div className="flex gap-2 mt-1 text-[10px]">
                    {m.group_joined_whatsapp && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">WA ✓</span>}
                    <span className="text-gray-500">{(m.group_assigned_at || '').slice(0, 10)}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => resend(m.user_id)}
                    disabled={busy}
                    title={lang === 'tr' ? 'Davet Tekrar Gonder' : 'Resend Invite'}
                    className="p-1.5 text-sky-700 hover:bg-sky-100 rounded disabled:opacity-50"
                    data-testid={`resend-${m.user_id}`}
                  >
                    <Send size={14} />
                  </button>
                  <button
                    onClick={() => remove(m.user_id)}
                    disabled={busy}
                    title={lang === 'tr' ? 'Gruptan Cikar' : 'Remove'}
                    className="p-1.5 text-red-600 hover:bg-red-100 rounded disabled:opacity-50"
                    data-testid={`remove-${m.user_id}`}
                  >
                    <UserMinus size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 pt-3 border-t text-xs">
          <a
            href={group.whatsapp_invite_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-700 hover:text-green-900 inline-flex items-center gap-1"
          >
            <ExternalLink size={12} />
            {lang === 'tr' ? 'WhatsApp Grubunu Ac' : 'Open WhatsApp Group'}
          </a>
        </div>
      </div>
    </div>
  );
}
